import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface Suggestion {
  label: string;
  action: string;
  priority: number;
}

/**
 * Fetches role- and context-aware suggestions from the agent-suggestions Edge Function.
 *
 * Re-fetches after staleTime expires (1 minute) so suggestions update naturally
 * after each conversation turn. The `enabled` parameter lets the caller pause
 * fetching while streaming is active.
 *
 * @param enabled - Whether the query should run (defaults to true)
 */
export function useAgentSuggestions(enabled: boolean = true) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["agent-suggestions", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<{ suggestions: Suggestion[] }>(
        "agent-suggestions",
      );
      if (error) throw new Error(error.message);
      return data?.suggestions ?? [];
    },
    enabled: enabled && !!profile?.id,
    staleTime: 60 * 1000, // 1 minute — suggestions update after each conversation turn
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}
